<template>
  <div v-loading="loading">
    <h1>Associate App Configuration</h1>
    <div class="mt-4">
      <AssociateAppToggle />

      <el-row v-if="isAssociateAppEnabled" class="pt-3 notes-container" :gutter="10">
        <el-col :span="24" :md="24">
          <CardView
            title="Associate App Features"
            :idx="0"
            :formData="formData"
            :formRules="rules"
            :isLoading="isLoading && cardToEditIdx === 0"
            @cardEditIdx="(idx) => (cardToEditIdx = idx)"
            @cancel="handleCancel"
            @save="saveFeatureSettings"
          >
            <template v-slot="{ isEditing }">
              <div class="text-item">
                <p class="mb-4">
                  Configure which features are available in the Associate App.
                </p>

                <div
                  v-for="(feature, index) in visibleFeatures"
                  :key="index"
                  class="mb-4"
                >
                  <div class="flex items-center mb-2">
                    <el-checkbox
                      v-model="feature.enabled"
                      @change="handleMainFeatureChange(feature)"
                      :disabled="!isEditing"
                    >
                      <h4 class="ml-2 text-gray-900">{{ feature.name }}</h4>
                    </el-checkbox>
                  </div>
                  <div class="ml-8">
                    <div
                      v-for="(subFeature, subIndex) in feature.subFeatures"
                      :key="subIndex"
                      class="mb-2"
                    >
                      <el-checkbox
                        v-model="subFeature.enabled"
                        :disabled="!isEditing"
                        @change="checkSubFeatureStatus(feature)"
                      >
                        <span class="text-gray-600">{{ subFeature.name }}</span>
                      </el-checkbox>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </CardView>
        </el-col>
      </el-row>

      <el-row v-if="isAssociateAppEnabled" class="pt-3 notes-container" :gutter="10">
        <el-col :span="24" :md="24">
          <CardView
            title="Associates Status Access"
            :idx="1"
            :formData="formData"
            :formRules="rules"
            :isLoading="isLoading && cardToEditIdx === 1"
            @cardEditIdx="(idx) => (cardToEditIdx = idx)"
            @cancel="handleCancel"
            @save="saveStatusSettings"
          >
            <template v-slot="{ isEditing }">
              <div class="text-item">
                <p class="mb-4">
                  Select which Associates statuses can access the Associate App.
                </p>

                <el-select
                  v-model="selectedStatuses"
                  multiple
                  placeholder="Select statuses"
                  style="width: 100%"
                  :disabled="!isEditing"
                >
                  <el-option
                    v-for="status in associateStatusOptions"
                    :key="status.value"
                    :label="status.label"
                    :value="status.value"
                  >
                  </el-option>
                </el-select>
              </div>
            </template>
          </CardView>
        </el-col>
      </el-row>
    </div>
  </div>
</template>

<script>
import CardView from "@/views/Settings/Users/components/CardView.vue";
import AssociateAppToggle from "@/views/Settings/AssociateApp/components/AssociateAppToggle.vue";
import { getTenantFeaturePermissions } from "@/middlewares/permissions";
import { mapState, mapGetters } from "vuex";
import { API } from "aws-amplify";

export default {
  name: "AssociateApp",
  components: {
    CardView,
    AssociateAppToggle,
  },
  computed: {
    ...mapState(["valuelists"]),
    ...mapGetters(["isSchedulingAndRosteringEnabled"]),
    featurePermissions() {
      return getTenantFeaturePermissions();
    },
    isAssociateAppEnabled() {
      return this.featurePermissions.permissionAssociateApp;
    },
    visibleFeatures() {
      const hiddenKeys = ['Messages'];
      if (!this.isSchedulingAndRosteringEnabled) {
        hiddenKeys.push('mySchedule', 'time-off-request');
      }
      return this.features.filter(f => !hiddenKeys.includes(f.key));
    },
    // Get staff statuses from the valuelists in the store
    associateStatusOptions() {
      if (!this.valuelists || !this.valuelists["staff-status"]) {
        return this.staffStatuses; // Fallback to hardcoded values
      }
      // Filter to only show non-deleted and non-hidden items
      const activeItems = this.valuelists["staff-status"].items.filter((item) => !item.deleted && !item.hidden);
      return activeItems;
    },
  },
  data() {
    return {
      loading: false,
      isLoading: false,
      cardToEditIdx: null,
      formData: {}, // Empty object for CardView component
      rules: {}, // Empty rules object for CardView component
      features: [
        {
          name: "Performance Metrics",
          key: "Performance",
          enabled: true,
          subFeatures: [],
        },
        {
          name: "Counselings",
          key: "Counselings",
          enabled: true,
          subFeatures: [
            
          ],
        },
        // {
        //   name: "Checklist",
        //   key: "Checklists",
        //   enabled: true,
        //   subFeatures: [
        //     {
        //       name: "Checklist Detail",
        //       key: "Checklists.checklist-detail",
        //       enabled: true,
        //     },
        //   ],
        // },
        {
          name: "Preferences",
          key: "Preferences",
          enabled: true,
          subFeatures: [],
        },
        {
          name: "Messages",
          key: "Messages",
          enabled: false,
          subFeatures: [],
        },
        {
          name: "Schedule",
          key: "mySchedule",
          enabled: true,
          subFeatures: [
            {
              name: "Assigned to me",
              key: "mySchedule.assigned-to-me",
              enabled: true,
            },
            {
              name: "Assigned to other",
              key: "mySchedule.assigned-to-other",
              enabled: true,
            },
            {
              name: "Not yet Assigned",
              key: "mySchedule.not-yet-assigned",
              enabled: true,
            },
          ],
        },
        {
          name: "Time Off Request",
          key: "time-off-request",
          enabled: true,
          subFeatures: [],
        },
      ],
      // Fallback statuses if valuelists are not available
      staffStatuses: [
        { label: "Active", value: "Active" },
      ],
      selectedStatuses: [],
      lastValidSelection: [],
      tenantId: "",
      tenantData: null,
    };
  },
  watch: {
    selectedStatuses(newVal, oldVal) {
      if (!newVal || newVal.length === 0) {
        this.$message.error("At least one status must remain selected");
        this.$nextTick(() => {
          this.selectedStatuses = [...this.lastValidSelection];
        });
      } else if (newVal.length > 0) {
        // Only update lastValidSelection if it's a real user change (not restoration)
        if (JSON.stringify(newVal) !== JSON.stringify(this.lastValidSelection)) {
          this.lastValidSelection = [...newVal];
        }
      }
    }
  },
  async mounted() {
      await this.loadTenantData(true);
  },
  methods: {
    async loadTenantData(isInitialLoad = false) {
      try {
        this.loading = isInitialLoad;
        // Get tenant ID from store or route
        this.tenantId = this.$store.state.userInfo.tenant.id;

        // Use the GET endpoint to fetch tenant data
        const response = await API.get("rds", `/tenant/${this.tenantId}`);

        if (response && response.success && response.data) {
          this.tenantData = response.data;
        } else {
          this.$message.error("Failed to load Associate App configuration");
        }

        // Load configuration from tenant data or use defaults
        this.loadConfiguration();
      } catch (error) {
        this.$message.error("Failed to load Associate App configuration");
        this.loadConfiguration();
      } finally {
        this.loading = false;
      }
    },

    loadConfiguration() {
      // Load features configuration
      if (this.tenantData?.allowedAssociateAppFeatures && this.tenantData.allowedAssociateAppFeatures.trim() !== "") {
        try {
          // Parse the comma-separated string
          const enabledFeaturesList =
            this.tenantData.allowedAssociateAppFeatures.split(",");

          // Reset all features to disabled first
          this.features.forEach((feature) => {
            feature.enabled = false;
            feature.subFeatures.forEach((subFeature) => {
              subFeature.enabled = false;
            });
          });

          // Enable features and sub-features based on the list
          this.features.forEach((feature) => {
            // Check if main feature is in the list
            if (enabledFeaturesList.includes(feature.key)) {
              feature.enabled = true;
            }

            // Check each sub-feature
            feature.subFeatures.forEach((subFeature) => {
              if (enabledFeaturesList.includes(subFeature.key)) {
                subFeature.enabled = true;
                // If a sub-feature is enabled, the main feature should be too
                feature.enabled = true;
              }
            });
          });
        } catch (error) {
          this.setDefaultConfiguration();
        }
      } else {
        // No configuration found, set defaults
        this.setDefaultConfiguration();
      }

      // Load selected statuses
      try {
        // Find the Active status option
        const activeOption = this.associateStatusOptions.find(option =>option.value === "Active" || option.value === "active");

        // Default value if no Active status is found
        const defaultStatus = activeOption ? activeOption.value : "Active";
        if (this.tenantData?.allowedAssociateAppStatuses) {
          const allowedStatuses = this.tenantData.allowedAssociateAppStatuses;
          
          // Handle comma-separated string format
          if (typeof allowedStatuses === "string" && allowedStatuses.includes(",")) {
            this.selectedStatuses = allowedStatuses
              .split(",")
              .filter(status => status.trim() !== "");
          }
          // Handle single value
          else if (typeof allowedStatuses === "string" && allowedStatuses.trim() !== "") {
            this.selectedStatuses = [allowedStatuses];
          }
          // Default to Active status if empty or invalid
          else {
            this.selectedStatuses = [defaultStatus];
          }
        } else {
          // No configuration found, default to Active status
          this.selectedStatuses = [defaultStatus];
        }
        
        // Initialize last valid selection
        this.lastValidSelection = [...this.selectedStatuses];

      } catch (error) {
        this.$message.error("Failed to load Associate App statuses");
      }
    },

    setDefaultConfiguration() {
      // Set all features and sub-features to enabled by default
      this.features.forEach((feature) => {
        feature.enabled = true;
        feature.subFeatures.forEach((subFeature) => {
          subFeature.enabled = true;
        });
      });

      // Select only Active status by default
      const activeOption = this.associateStatusOptions.find(
        option => option.value === "Active" || option.value === "active"
      );
      
      if (activeOption) {
        this.selectedStatuses = [activeOption.value];
      } else if (this.associateStatusOptions.length > 0) {
        // Fallback to first status if Active not found
        this.selectedStatuses = [this.associateStatusOptions[0].value];
      } else {
        // Ultimate fallback
        this.selectedStatuses = ["Active"];
      }

    },

    async handleCancel() {
      await this.loadTenantData(false);
      this.cardToEditIdx = null;
    },

    handleMainFeatureChange(feature) {
      // If main feature is disabled, disable all sub-features
      if (!feature.enabled) {
        feature.subFeatures.forEach((subFeature) => {
          subFeature.enabled = false;
        });
      }
      // If main feature is enabled, handle sub-features
      else if (feature.subFeatures.length > 0) {
        // When parent feature is enabled, enable all sub-features by default
        feature.subFeatures.forEach((subFeature) => {
          subFeature.enabled = true;
        });
      }
    },

    checkSubFeatureStatus(feature) {
      // Skip features with no sub-features
      if (!feature.subFeatures || feature.subFeatures.length === 0) return;

      // Check if any sub-feature is enabled
      const anySubFeatureEnabled = feature.subFeatures.some(
        (subFeature) => subFeature.enabled
      );

      // If any sub-feature is enabled, make sure the main feature is enabled too
      if (anySubFeatureEnabled && !feature.enabled) {
        feature.enabled = true;
      }

      // If all sub-features are disabled, disable the main feature
      const allSubFeaturesDisabled = feature.subFeatures.every(
        (subFeature) => !subFeature.enabled
      );

      if (allSubFeaturesDisabled && feature.enabled) {
        feature.enabled = false;
      }
    },



    async saveFeatureSettings() {
      this.isLoading = true;
      try {
        // Create a comma-separated list of enabled features and their sub-features
        const enabledFeaturesList = [];

        this.features.forEach((feature) => {
          if (feature.enabled) {
            // Add the main feature key
            enabledFeaturesList.push(feature.key);

            // Add enabled sub-features
            feature.subFeatures.forEach((subFeature) => {
              if (subFeature.enabled) {
                enabledFeaturesList.push(subFeature.key);
              }
            });
          }
        });

        // Join the list with commas
        const featuresString =enabledFeaturesList.length==0 ? "": enabledFeaturesList.join(",");
        // Use PATCH to update the tenant
        const response = await API.put("rds", `/tenant/${this.tenantId}`, {
          body: {
            allowedAssociateAppFeatures: featuresString,
          },
        });

        if (response && response.success) {
          this.$message({
            message: "Feature settings saved successfully",
            type: "success",
          });
          this.cardToEditIdx = null;
        } else {
          throw new Error("Failed to save feature settings");
        }
      } catch (error) {
        this.$message.error("Failed to save feature settings");
      } finally {
        this.isLoading = false;
      }
    },



    async saveStatusSettings() {
      this.isLoading = true;

      try {
        
        // Format the statuses as a comma-separated string
        const statusesString = this.selectedStatuses.join(",");
        // Use PATCH to update the tenant
        const response = await API.put("rds", `/tenant/${this.tenantId}`, {
          body: {
            allowedAssociateAppStatuses: statusesString,
          },
        });

        if (response && response.success) {
          this.$message({
            message: "Status settings saved successfully",
            type: "success",
          });
          this.cardToEditIdx = null;
        } else {
          throw new Error("Failed to save status settings");
        }
      } catch (error) {
        this.$message.error("Failed to save status settings");
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.text-item {
  margin-bottom: 18px;
}

.large-checkbox {
  transform: scale(1.1);
  transform-origin: left center;
}

.large-checkbox .el-checkbox__input {
  transform: scale(1.1);
}
</style>
